import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppContext } from '../contexts/AppContext.tsx';
import { ActualSalesService, AssistDetailData } from '../services/actualSalesService.ts';
import { ActualSalesDebugLogger, PerformanceTracker, DataValidator } from '../utils/debugUtils.ts';

// 훅 반환 타입 정의
export interface UseActualSalesDataResult {
  teamActualSales: Record<string, number>;
  totalActualSales: number;
  assistDetails: Record<string, AssistDetailData>;
  isLoading: boolean;
  isCalculating: boolean;
  dataSource: 'cached' | 'teamCollection_based' | 'empty';
  lastCalculated: string;
  error: string | null;
  // targetMonth 지정 시 해당 월을 재계산·저장 (미지정 시 화면 표시 월)
  refreshData: (targetMonth?: string) => Promise<void>;
}

/**
 * 실제매출 데이터 자동화 훅 (teamCollection 기반)
 * 캐시 우선 로딩 + teamCollection 기반 자동 계산 + Firebase 저장
 * 
 * @param yearMonth 조회할 년월 (YYYY-MM 형식)
 * @returns 실제매출 데이터 및 관리 함수들
 */
export const useActualSalesData = (yearMonth: string): UseActualSalesDataResult => {
  const { employees, budgetData, setBudgetData, isLoading: contextLoading } = useAppContext();

  // 중복 실행 방지를 위한 ref
  const isLoadingRef = useRef(false);
  // refreshData 전용 중복 실행 가드 (setState updater 내부 가드는 실행을 막지 못함)
  const isRefreshingRef = useRef(false);

  // stale closure 방지를 위한 ref들
  const budgetDataRef = useRef(budgetData);
  budgetDataRef.current = budgetData;

  const setBudgetDataRef = useRef(setBudgetData);
  setBudgetDataRef.current = setBudgetData;

  const [actualSalesState, setActualSalesState] = useState({
    teamActualSales: {} as Record<string, number>,
    totalActualSales: 0,
    assistDetails: {} as Record<string, AssistDetailData>,
    isLoading: true,
    isCalculating: false,
    dataSource: 'empty' as 'cached' | 'teamCollection_based' | 'empty',
    lastCalculated: '',
    error: null as string | null
  });

  // 실제매출 데이터 로드 또는 계산 (teamCollection 기반)
  const loadOrCalculateActualSales = useCallback(async () => {
    ActualSalesDebugLogger.hook(`데이터 로딩 시작 - yearMonth: ${yearMonth}`, {
      contextLoading,
      employeeCount: employees.length,
      hasBudgetData: !!budgetDataRef.current,
      currentState: actualSalesState
    });

    if (contextLoading || !employees.length || !yearMonth) {
      console.log('[DEBUG] Hook blocked - conditions not met:', {
        contextLoading,
        employeesLength: employees.length,
        yearMonth,
        blockingCondition: contextLoading ? 'contextLoading' : (!employees.length ? 'no employees' : 'no yearMonth')
      });
      ActualSalesDebugLogger.hook('필수 데이터 로딩 중 또는 미완료', {
        contextLoading,
        employeeCount: employees.length,
        yearMonth
      });
      return;
    }

    // ref를 사용한 중복 실행 방지
    if (isLoadingRef.current) {
      ActualSalesDebugLogger.hook('이미 데이터 로딩/계산 중 - 중복 실행 방지 (ref)', {
        isLoadingRef: isLoadingRef.current
      });
      return;
    }

    // 로딩 시작
    isLoadingRef.current = true;
    ActualSalesDebugLogger.hook('데이터 로딩 상태 변경: isLoading = true');
    setActualSalesState(prev => ({ ...prev, isLoading: true, error: null }));
    
    PerformanceTracker.start('loadOrCalculateActualSales');
    
    try {
      ActualSalesDebugLogger.step(1, `${yearMonth} teamCollection 기반 실제매출 데이터 로드/계산 시작`);
      
      // 1. 먼저 캐시된 데이터 확인 (유효성 검증 포함)
      ActualSalesDebugLogger.cache('캐시된 데이터 확인 시작 (유효성 검증 포함)');
      const cachedData = await ActualSalesService.loadCachedActualSales(yearMonth, budgetDataRef.current);
      
      if (cachedData) {
        // 검증된 캐시된 데이터 사용
        ActualSalesDebugLogger.cache(`${yearMonth} 검증된 캐시 데이터 사용`, {
            teamCount: Object.keys(cachedData.teamActualSales).length,
            totalSales: cachedData.totalActualSales,
            assistCount: Object.keys(cachedData.assistDetails).length,
            calculatedAt: cachedData.calculatedAt,
            isValidated: cachedData.isValidated
          });

        // 캐시된 데이터 구조 유효성 검사 (Firebase 시간 비교는 이미 완료)
        const isCacheStructureValid = DataValidator.validateTeamActualSales(cachedData.teamActualSales) &&
                                    DataValidator.validateAssistDetails(cachedData.assistDetails);
        
        if (isCacheStructureValid) {
          setActualSalesState(prev => ({
            ...prev,
            teamActualSales: cachedData.teamActualSales,
            totalActualSales: cachedData.totalActualSales,
            assistDetails: cachedData.assistDetails,
            lastCalculated: cachedData.calculatedAt,
            dataSource: 'cached',
            isLoading: false
          }));
          
          ActualSalesDebugLogger.hook('검증된 캐시 데이터 사용 완료', {
            dataSource: 'cached',
            teamCount: Object.keys(cachedData.teamActualSales).length,
            totalSales: cachedData.totalActualSales.toLocaleString(),
            isValidated: cachedData.isValidated
          });
        } else {
          ActualSalesDebugLogger.cache('캐시 데이터 구조 유효성 검사 실패 - 재계산 진행');
        }
      } else {
        ActualSalesDebugLogger.cache(`${yearMonth} 캐시된 데이터 없음 또는 무효 - 실시간 계산 진행`);
        
        // teamCollection 기반 실시간 계산
        ActualSalesDebugLogger.step(2, `${yearMonth} teamCollection 기반 실시간 계산 시작`);
        setActualSalesState(prev => {
          ActualSalesDebugLogger.hook('계산 상태 변경: isCalculating = true');
          return { ...prev, isCalculating: true, dataSource: 'teamCollection_based' };
        });
        
        const calculatedData = await ActualSalesService.calculateMonthlyActualSales(
          yearMonth,
          employees,
          budgetDataRef.current  // 🔧 FIX: ref로 최신 budgetData 참조
        );

        ActualSalesDebugLogger.step(3, '실시간 계산 완료', {
          teamCount: Object.keys(calculatedData.teamActualSales).length,
          totalSales: calculatedData.totalActualSales.toLocaleString(),
          assistCount: Object.keys(calculatedData.assistDetails).length
        });

        // 계산된 데이터 유효성 검사
        const isCalculatedValid = DataValidator.validateTeamActualSales(calculatedData.teamActualSales) &&
                                DataValidator.validateAssistDetails(calculatedData.assistDetails);
        
        if (isCalculatedValid) {
          setActualSalesState(prev => ({
            ...prev,
            teamActualSales: calculatedData.teamActualSales,
            totalActualSales: calculatedData.totalActualSales,
            assistDetails: calculatedData.assistDetails,
            lastCalculated: calculatedData.calculatedAt,
            dataSource: 'teamCollection_based',
            isCalculating: false
          }));
          
          ActualSalesDebugLogger.hook('계산 데이터 적용 완료', {
            dataSource: 'teamCollection_based',
            teamCount: Object.keys(calculatedData.teamActualSales).length,
            totalSales: calculatedData.totalActualSales.toLocaleString()
          });
        } else {
          ActualSalesDebugLogger.error('계산된 데이터 유효성 검사 실패');
          throw new Error('계산된 데이터가 유효하지 않습니다');
        }
        
        // 자동 저장 (백그라운드)
        try {
          ActualSalesDebugLogger.step(4, '자동 저장 시작');
          await ActualSalesService.saveActualSalesData(
            yearMonth,
            calculatedData,
            budgetDataRef.current,
            setBudgetDataRef.current
          );
          ActualSalesDebugLogger.hook(`${yearMonth} teamCollection 기반 자동 저장 완료`);
        } catch (saveError) {
          ActualSalesDebugLogger.error(`${yearMonth} teamCollection 기반 자동 저장 실패`, saveError);
          // 저장 실패해도 계산된 데이터는 표시
        }
        
        ActualSalesDebugLogger.step(5, `${yearMonth} teamCollection 기반 실시간 계산 완료`);
      }
      
    } catch (err) {
      ActualSalesDebugLogger.error('teamCollection 기반 데이터 로드/계산 실패', err);
      setActualSalesState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'teamCollection 기반 데이터 로드 실패',
        dataSource: 'empty'
      }));
    } finally {
      const duration = PerformanceTracker.end('loadOrCalculateActualSales');
      ActualSalesDebugLogger.hook('데이터 로딩 완료', {
        duration: `${duration}ms`,
        finalState: {
          isLoading: false,
          isCalculating: false
        }
      });

      // ref 초기화 및 상태 업데이트
      isLoadingRef.current = false;
      setActualSalesState(prev => ({
        ...prev,
        isLoading: false,
        isCalculating: false
      }));
    }
  }, [yearMonth, employees, contextLoading]); // 🔧 FIX: budgetData 제거 (ref로 추적), actualSalesState는 로깅용이므로 의존성에서 제외

  // 데이터 새로고침 (강제 재계산) - teamCollection 기반
  // targetMonth 지정 시(엑셀 업로드 자동화 등) 해당 월을 우선 재계산·저장
  const refreshData = useCallback(async (targetMonth?: string) => {
    const effectiveMonth = targetMonth || yearMonth;

    ActualSalesDebugLogger.hook(`데이터 새로고침 시작 - yearMonth: ${effectiveMonth}`, {
      contextLoading,
      employeeCount: employees.length,
      currentState: actualSalesState
    });

    if (contextLoading || !employees.length || !effectiveMonth) {
      ActualSalesDebugLogger.hook('refreshData: 필수 데이터 미완료', {
        contextLoading,
        employeeCount: employees.length,
        yearMonth: effectiveMonth
      });
      return;
    }

    // 이미 계산 중이면 중복 실행 방지 (ref 가드 — 함수 초입에서 조기 return)
    if (isRefreshingRef.current) {
      ActualSalesDebugLogger.hook('이미 계산 중 - 새로고침 중복 실행 방지');
      return;
    }
    isRefreshingRef.current = true;

    ActualSalesDebugLogger.hook('새로고침 상태 변경: isCalculating = true');
    setActualSalesState(prev => ({ ...prev, isCalculating: true, error: null }));

    PerformanceTracker.start('refreshData');

    try {
      ActualSalesDebugLogger.step(1, `${effectiveMonth} teamCollection 기반 강제 새로고침 시작`);

      const calculatedData = await ActualSalesService.calculateMonthlyActualSales(
        effectiveMonth,
        employees,
        budgetDataRef.current  // 🔧 FIX: ref로 최신 budgetData 참조
      );

      ActualSalesDebugLogger.step(2, '강제 새로고침 계산 완료', {
        teamCount: Object.keys(calculatedData.teamActualSales).length,
        totalSales: calculatedData.totalActualSales.toLocaleString(),
        assistCount: Object.keys(calculatedData.assistDetails).length
      });

      // 새로고침된 데이터 유효성 검사
      const isRefreshValid = DataValidator.validateTeamActualSales(calculatedData.teamActualSales) &&
                           DataValidator.validateAssistDetails(calculatedData.assistDetails);
      
      if (isRefreshValid) {
        // 화면 표시 월과 같을 때만 로컬 상태 반영 (다른 월 업로드 새로고침은 저장만 수행)
        if (effectiveMonth === yearMonth) {
          setActualSalesState(prev => ({
            ...prev,
            teamActualSales: calculatedData.teamActualSales,
            totalActualSales: calculatedData.totalActualSales,
            assistDetails: calculatedData.assistDetails,
            lastCalculated: calculatedData.calculatedAt,
            dataSource: 'teamCollection_based'
          }));
        }

        ActualSalesDebugLogger.hook('새로고침 데이터 적용 완료', {
          dataSource: 'teamCollection_based',
          teamCount: Object.keys(calculatedData.teamActualSales).length,
          totalSales: calculatedData.totalActualSales.toLocaleString()
        });
      } else {
        ActualSalesDebugLogger.error('새로고침된 데이터 유효성 검사 실패');
        throw new Error('새로고침된 데이터가 유효하지 않습니다');
      }

      // 저장 (백그라운드)
      try {
        ActualSalesDebugLogger.step(3, '새로고침 데이터 저장 시작');
        await ActualSalesService.saveActualSalesData(
          effectiveMonth,
          calculatedData,
          budgetDataRef.current,
          setBudgetDataRef.current
        );
        ActualSalesDebugLogger.hook(`${effectiveMonth} teamCollection 기반 강제 새로고침 저장 완료`);
      } catch (saveError) {
        ActualSalesDebugLogger.error(`${effectiveMonth} teamCollection 기반 강제 새로고침 저장 실패`, saveError);
        // 저장 실패해도 계산된 데이터는 표시
      }

      ActualSalesDebugLogger.step(4, `${effectiveMonth} teamCollection 기반 강제 새로고침 완료`);

    } catch (err) {
      ActualSalesDebugLogger.error('teamCollection 기반 새로고침 실패', err);
      setActualSalesState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'teamCollection 기반 새로고침 실패'
      }));
    } finally {
      const duration = PerformanceTracker.end('refreshData');
      ActualSalesDebugLogger.hook('새로고침 완료', {
        duration: `${duration}ms`,
        finalState: {
          isCalculating: false
        }
      });

      isRefreshingRef.current = false;
      setActualSalesState(prev => ({ ...prev, isCalculating: false }));
    }
  }, [yearMonth, employees, contextLoading]); // 🔧 FIX: budgetData 제거 (ref로 추적), actualSalesState는 로깅용이므로 의존성에서 제외

  // 월 변경 또는 초기 로드 시 데이터 로드
  useEffect(() => {
    ActualSalesDebugLogger.hook('useEffect 트리거 - 데이터 로드 시작', {
      yearMonth,
      employeeCount: employees.length,
      contextLoading
    });

    // 필수 조건을 만족할 때만 데이터 로드 실행
    if (!contextLoading && employees.length > 0 && yearMonth) {
      loadOrCalculateActualSales();
    }
  }, [contextLoading, employees.length, yearMonth]); // loadOrCalculateActualSales 의존성 제거하여 무한 리로딩 방지
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // 상태 변경 감지 및 로깅
  useEffect(() => {
    ActualSalesDebugLogger.hook('상태 변경 감지', {
      isLoading: actualSalesState.isLoading,
      isCalculating: actualSalesState.isCalculating,
      dataSource: actualSalesState.dataSource,
      teamCount: Object.keys(actualSalesState.teamActualSales).length,
      totalSales: actualSalesState.totalActualSales.toLocaleString(),
      assistCount: Object.keys(actualSalesState.assistDetails).length,
      hasError: !!actualSalesState.error
    });
  }, [actualSalesState]);

  ActualSalesDebugLogger.hook('useActualSalesData 훅 리턴값 준비', {
    yearMonth,
    returnData: {
      teamCount: Object.keys(actualSalesState.teamActualSales).length,
      totalSales: actualSalesState.totalActualSales.toLocaleString(),
      assistCount: Object.keys(actualSalesState.assistDetails).length,
      isLoading: actualSalesState.isLoading,
      isCalculating: actualSalesState.isCalculating,
      dataSource: actualSalesState.dataSource,
      hasError: !!actualSalesState.error
    }
  });

  return {
    teamActualSales: actualSalesState.teamActualSales || {},
    totalActualSales: actualSalesState.totalActualSales || 0,
    assistDetails: actualSalesState.assistDetails || {},
    isLoading: actualSalesState.isLoading,
    isCalculating: actualSalesState.isCalculating,
    dataSource: actualSalesState.dataSource,
    lastCalculated: actualSalesState.lastCalculated,
    error: actualSalesState.error,
    refreshData
  };
}; 